import { useState } from 'react';

interface Ministry {
  id: number;
  name: string;
  description: string;
  icon: string;
  image: string;
  leader: string;
  contact: string;
  programs: string[];
  meetingTime?: string;
  location?: string;
  fullDescription?: string;
  objectives?: string[];
  howToJoin?: string;
}

interface MinistryModalProps {
  ministry: Ministry;
  isOpen: boolean;
  onClose: () => void;
}

export default function MinistryModal({ ministry, isOpen, onClose }: MinistryModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'programs' | 'contact'>('overview');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header with Image */}
          <div className="relative h-64 overflow-hidden">
            <img 
              src={ministry.image}
              alt={ministry.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                  <i className={`${ministry.icon} text-white text-2xl`}></i>
                </div>
                <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                  {ministry.name}
                </h2>
              </div>
              <p className="text-white/90 text-sm">{ministry.description}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'overview'
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-information-line mr-2"></i>
                Overview
              </button>
              <button
                onClick={() => setActiveTab('programs')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'programs'
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-calendar-line mr-2"></i>
                Programs
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'contact'
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-contacts-line mr-2"></i>
                Contact
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-400px)]">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-file-text-line text-emerald-600 mr-2"></i>
                    About This Ministry
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {ministry.fullDescription || ministry.description}
                  </p>
                </div>

                {ministry.objectives && ministry.objectives.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-target-line text-emerald-600 mr-2"></i>
                      Our Objectives
                    </h3>
                    <ul className="space-y-2">
                      {ministry.objectives.map((objective, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                          <span className="text-gray-700">{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {ministry.howToJoin && (
                  <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-user-add-line text-emerald-600 mr-2"></i>
                      How to Get Involved
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {ministry.howToJoin}
                    </p>
                    <button className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-hand-heart-line mr-2"></i>
                      Join This Ministry
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Programs Tab */}
            {activeTab === 'programs' && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-calendar-check-line text-emerald-600 mr-2"></i>
                  Ministry Programs & Activities
                </h3>
                
                {ministry.meetingTime && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Regular Meeting Time</p>
                        <p className="font-semibold text-gray-900">{ministry.meetingTime}</p>
                      </div>
                    </div>
                  </div>
                )}

                {ministry.location && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Meeting Location</p>
                        <p className="font-semibold text-gray-900">{ministry.location}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-3">
                  {ministry.programs.map((program, index) => (
                    <div 
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-s-line text-emerald-600"></i>
                        </div>
                        <span className="font-medium text-gray-900">{program}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-contacts-book-line text-emerald-600 mr-2"></i>
                  Get in Touch
                </h3>

                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-emerald-600 text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Ministry Leader</p>
                        <p className="font-semibold text-gray-900 text-lg">{ministry.leader}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Email Address</p>
                        <a 
                          href={`mailto:${ministry.contact}`}
                          className="font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                        >
                          {ministry.contact}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
                  <h4 className="font-bold text-gray-900 mb-3">Send a Message</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Have questions or want to learn more? Reach out to us directly.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`mailto:${ministry.contact}`}
                      className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-send-line mr-2"></i>
                      Send Email
                    </a>
                    <button className="px-5 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-whatsapp-line mr-2"></i>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
            <div className="flex gap-2">
              <button className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
              <button className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-user-add-line mr-2"></i>
                Join Ministry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
